/**
 * SmartListSelector Component
 * 
 * Dropdown to select, manage, and apply Smart Lists (saved filter configurations).
 */

import React, { useState } from 'react';
import {
  HStack,
  Text,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroup,
  Badge,
  useDisclosure,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiList,
  FiChevronDown,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiStar,
  FiGlobe,
  FiUser,
  FiCopy,
} from 'react-icons/fi';
import SmartListEditor from './SmartListEditor';

/**
 * SmartListSelector - Dropdown for selecting and managing Smart Lists
 */
export default function SmartListSelector({
  smartLists = [],
  personalLists = [],
  globalLists = [],
  selectedListId,
  onSelectList,
  onCreateList,
  onUpdateList,
  onDeleteList,
  onDuplicateList,
  onSetDefault,
  onUnsetDefault,
  currentFilters = [],
  currentSort = {},
  visibleColumns = [],
  entityType = 'contacts',
  loading = false,
}) {
  const { isOpen: isEditorOpen, onOpen: onEditorOpen, onClose: onEditorClose } = useDisclosure();
  const [editingList, setEditingList] = useState(null);
  
  const menuBg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  
  // Get the currently selected list
  const selectedList = smartLists.find(l => l.id === selectedListId);
  
  // Handle list selection
  const handleSelectList = (list) => {
    if (list) {
      onSelectList(list.id, list.filters, list.sortBy, list.sortDirection, list.visibleColumns);
    } else {
      onSelectList(null, [], null, null, []);
    }
  };
  
  // Open editor to create new list
  const handleCreateNew = () => {
    setEditingList(null);
    onEditorOpen();
  };
  
  // Open editor to edit existing list
  const handleEdit = (list, e) => {
    e.stopPropagation();
    setEditingList(list);
    onEditorOpen();
  };
  
  // Handle save from editor
  const handleSave = async (listData) => {
    if (editingList) {
      await onUpdateList(editingList.id, listData);
    } else {
      await onCreateList(listData);
    }
    onEditorClose();
  };
  
  // Handle delete
  const handleDelete = async (list, e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${list.name}"?`)) {
      await onDeleteList(list.id);
    }
  };
  
  // Handle duplicate
  const handleDuplicate = async (list, e) => {
    e.stopPropagation();
    await onDuplicateList(list.id);
  };
  
  // Handle set as default
  const handleSetDefault = async (list, e) => {
    e.stopPropagation();
    await onSetDefault(list.id);
  };
  
  // Handle unset default
  const handleUnsetDefault = async (list, e) => {
    e.stopPropagation();
    if (onUnsetDefault) {
      await onUnsetDefault(list.id);
    }
  };
  
  // Save current filters as new Smart List
  const handleSaveCurrentAsNew = () => {
    setEditingList({
      name: '',
      description: '',
      filters: currentFilters,
      sortBy: currentSort.field,
      sortDirection: currentSort.direction,
      visibleColumns: visibleColumns,
      isGlobal: false,
    });
    onEditorOpen();
  };
  
  const renderListItem = (list, showActions = true) => (
    <MenuItem
      key={list.id}
      onClick={() => handleSelectList(list)}
      bg={selectedListId === list.id ? hoverBg : 'transparent'}
      _hover={{ bg: hoverBg }}
    >
      <HStack justify="space-between" width="100%">
        <HStack spacing={2}>
          {list.isDefault && (
            <Tooltip label="Default list">
              <span><FiStar color="gold" size={12} /></span>
            </Tooltip>
          )}
          <Text fontSize="sm">{list.name}</Text>
          {list.isGlobal && (
            <Badge colorScheme="purple" size="sm" fontSize="xs">
              Shared
            </Badge>
          )}
        </HStack>
        
        {showActions && (
          <HStack spacing={0}>
            <Tooltip label="Edit">
              <IconButton
                icon={<FiEdit2 />}
                size="xs"
                variant="ghost"
                aria-label="Edit list"
                onClick={(e) => handleEdit(list, e)}
              />
            </Tooltip>
            <Tooltip label="Duplicate">
              <IconButton
                icon={<FiCopy />}
                size="xs"
                variant="ghost"
                aria-label="Duplicate list"
                onClick={(e) => handleDuplicate(list, e)}
              />
            </Tooltip>
            {list.isDefault ? (
              <Tooltip label="Unset default">
                <IconButton
                  icon={<FiStar color="gold" />}
                  size="xs"
                  variant="ghost"
                  aria-label="Unset default"
                  onClick={(e) => handleUnsetDefault(list, e)}
                />
              </Tooltip>
            ) : (
              <Tooltip label="Set as default">
                <IconButton
                  icon={<FiStar />}
                  size="xs"
                  variant="ghost"
                  aria-label="Set as default"
                  onClick={(e) => handleSetDefault(list, e)}
                />
              </Tooltip>
            )}
            <Tooltip label="Delete">
              <IconButton
                icon={<FiTrash2 />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                aria-label="Delete list"
                onClick={(e) => handleDelete(list, e)}
              />
            </Tooltip>
          </HStack>
        )}
      </HStack>
    </MenuItem>
  );
  
  return (
    <>
      <Menu closeOnSelect={false}>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          leftIcon={<FiList />}
          rightIcon={<FiChevronDown />}
          isLoading={loading}
        >
          {selectedList ? selectedList.name : 'Smart Lists'}
        </MenuButton>
        
        <MenuList bg={menuBg} minW="280px" maxH="400px" overflowY="auto" zIndex={1500}>
          {/* Clear selection */}
          <MenuItem
            onClick={() => handleSelectList(null)}
            fontStyle={!selectedListId ? 'italic' : 'normal'}
          >
            <Text fontSize="sm" color="gray.500">
              Clear selection (show all)
            </Text>
          </MenuItem>
          
          <MenuDivider />
          
          {/* Personal Lists */}
          {personalLists.length > 0 && (
            <>
              <MenuGroup title={
                <HStack spacing={1}>
                  <FiUser size={12} />
                  <Text>My Lists</Text>
                </HStack>
              }>
                {personalLists.map(list => renderListItem(list))}
              </MenuGroup>
              <MenuDivider />
            </>
          )}
          
          {/* Global/Shared Lists */}
          {globalLists.length > 0 && (
            <>
              <MenuGroup title={
                <HStack spacing={1}>
                  <FiGlobe size={12} />
                  <Text>Shared Lists</Text>
                </HStack>
              }>
                {globalLists.map(list => renderListItem(list, false))}
              </MenuGroup>
              <MenuDivider />
            </>
          )}
          
          {/* No lists message */}
          {smartLists.length === 0 && (
            <MenuItem isDisabled>
              <Text fontSize="sm" color="gray.500">
                No saved lists yet
              </Text>
            </MenuItem>
          )}
          
          <MenuDivider />
          
          {/* Create new */}
          <MenuItem
            icon={<FiPlus />}
            onClick={handleCreateNew}
          >
            Create New Smart List
          </MenuItem>
          
          {/* Save current filters */}
          {currentFilters.length > 0 && (
            <MenuItem
              icon={<FiPlus />}
              onClick={handleSaveCurrentAsNew}
            >
              Save Current Filters as List
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      
      {/* Editor Modal */}
      <SmartListEditor
        isOpen={isEditorOpen}
        onClose={onEditorClose}
        list={editingList}
        onSave={handleSave}
        entityType={entityType}
        currentFilters={editingList ? editingList.filters : currentFilters}
        currentSort={editingList ? { field: editingList.sortBy, direction: editingList.sortDirection } : currentSort}
        visibleColumns={editingList ? editingList.visibleColumns : visibleColumns}
      />
    </>
  );
}
